import { Select, Modal, Typography, notification } from 'antd';
import { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { i18n } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { billingModel } from '@/entities/billing';

import { useBillingZonesOptions } from '../hooks/useBillingZonesOptions';

import styles from './SelectBillingZone.module.scss';

type SelectBillingZoneProps = {
  canEditAccountSettings: boolean;
};

export const SelectBillingZone: FC<SelectBillingZoneProps> = (props) => {
  const { canEditAccountSettings } = props;

  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { ID: accountID, BillingZones } = useAppSelector(
    billingModel.selectors.selectAccountInfo,
  );
  const billingZonesOptions = useBillingZonesOptions();
  const currentBillingZone = BillingZones?.at(0);

  const changeBillingZone = useCallback(
    async (newBillingZone: string) => {
      setLoading(true);

      try {
        await dispatch(
          billingModel.thunks.setAccountBillingZone({
            AccountID: accountID,
            BillingZones: [newBillingZone],
          }),
        ).unwrap();

        notification.success({
          placement: 'bottomLeft',
          message: formatMessage({
            id: 'userProfile.changeBillingZoneSuccessMessage',
            defaultMessage: 'Billing zone successfully changed',
          }),
        });
      } catch (error) {
        notification.error({ message: error?.message });
      } finally {
        setLoading(false);
      }
    },
    [accountID],
  );

  const showConfirmChangeBillingZone = (newBillingZone: string) => {
    Modal.confirm({
      // NOTE: For some reason title throw ts error because we use values to get billing zone in warning window
      // @ts-ignore
      title: formatMessage(
        {
          id: 'userProfile.changeBillingZoneConfirm',
          defaultMessage:
            'Are you sure you want to change the billing zone to {billingZone} for this account?',
        },
        {
          billingZone: <b>{newBillingZone}</b>,
        },
      ),
      cancelText: formatMessage(i18n.modal.no),
      okText: formatMessage(i18n.modal.yes),
      onOk: () => changeBillingZone(newBillingZone),
    });
  };

  return (
    <>
      <span className={styles.label}>
        <FormattedMessage
          id="userProfile.billingZone"
          defaultMessage="Billing Zone"
        />
      </span>
      <div className={styles.value}>
        <Select
          value={currentBillingZone}
          options={billingZonesOptions}
          loading={loading}
          disabled={!canEditAccountSettings}
          onChange={showConfirmChangeBillingZone}
        />
        <Typography.Text type="warning" className={styles.warning}>
          <FormattedMessage
            id="userProfile.billingZoneWarning"
            defaultMessage="Changing the billing zone will impact the customer's current subscription, package, and financial reporting. To safely implement the change, please remove the existing subscription, change the billing zone and add a new subscription and required packages."
          />
        </Typography.Text>
      </div>
    </>
  );
};
